import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getCobroPorTramite } from '../../services/cobranzaService';
import { actualizarPactoTramite } from '../../services/tramiteService';
import BotonGenerarPacto from '../Pacto/BotonGenerarPacto';

/**
 * Editor del pacto de honorarios (cantidad de haberes / 30%) en la ficha del trámite, mientras
 * todavía no existe el cobro — se guarda en el trámite (PATCH /api/tramites/{id}/pacto) y se
 * copia al cobro recién cuando este nace, al cerrar favorable (ver CambioEstadoService).
 *
 * Una vez que el cobro existe, la gestión pasa a vivir en SeccionCobranzaTramite (entre Turno
 * ANSES y Eventos): esta tarjeta se retira para no duplicar la información.
 *
 * La ven y la editan todos los roles (23-oct-2026): el pacto se pacta en el mostrador, antes del
 * favorable, y hasta acá solo lo podía corregir administración. "Generar pacto" emite el
 * documento con los haberes y el 30% cargados en la tarjeta (ver BotonGenerarPacto) y avisa con
 * `onPactoGenerado` para que la ficha refresque Archivos y el checklist.
 */
export default function TarjetaCobroTramite({ tramite, onPactoGenerado }) {
    const idTramite = tramite?.idTramite;
    const [tieneCobro, setTieneCobro] = useState(null);
    const [pacto, setPacto] = useState({ cantidadHaberes: '', conPorcentaje: false });
    const [guardandoPacto, setGuardandoPacto] = useState(false);

    useEffect(() => {
        setPacto({
            cantidadHaberes: tramite?.pactoCantidadHaberes ?? '',
            conPorcentaje: !!tramite?.pactoConPorcentaje,
        });
    }, [tramite]);

    useEffect(() => {
        if (!idTramite) return;
        getCobroPorTramite(idTramite)
            .then((c) => setTieneCobro(!!c))
            .catch(() => setTieneCobro(false));
    }, [idTramite]);

    const guardarPacto = async (cambios) => {
        if (!idTramite) return;
        setGuardandoPacto(true);
        try {
            await actualizarPactoTramite(idTramite, cambios);
        } catch (err) {
            toast.error('No se pudo guardar el pacto.');
        } finally {
            setGuardandoPacto(false);
        }
    };

    if (tieneCobro !== false) return null;

    return (
        <div
            className="mt-3"
            style={{ border: '1px dashed #cbd5e1', borderRadius: '10px', padding: '0.75rem 1rem', background: '#f9fafb' }}
        >
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                <i className="bi bi-cash-coin me-2" style={{ color: '#6b7280' }}></i>Pacto de honorarios
            </div>
            <div className="d-flex align-items-end gap-3 flex-wrap">
                <div>
                    <label className="form-label small text-muted mb-1">Cantidad de haberes</label>
                    <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: 160 }}
                        value={pacto.cantidadHaberes}
                        disabled={guardandoPacto}
                        onChange={(e) => setPacto((p) => ({ ...p, cantidadHaberes: e.target.value }))}
                        onBlur={() => guardarPacto({ cantidadHaberes: pacto.cantidadHaberes === '' ? null : Number(pacto.cantidadHaberes) })}
                    />
                </div>
                <div className="form-check mb-1">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id={`conPorcentaje-${idTramite}`}
                        checked={pacto.conPorcentaje}
                        disabled={guardandoPacto}
                        onChange={(e) => {
                            const conPorcentaje = e.target.checked;
                            setPacto((p) => ({ ...p, conPorcentaje }));
                            guardarPacto({ conPorcentaje });
                        }}
                    />
                    <label className="form-check-label" htmlFor={`conPorcentaje-${idTramite}`}>Con 30%</label>
                </div>
                <BotonGenerarPacto
                    idTramite={idTramite}
                    cantidadHaberes={pacto.cantidadHaberes}
                    conPorcentaje={pacto.conPorcentaje}
                    onGenerado={onPactoGenerado}
                />
            </div>
        </div>
    );
}
